import { providerTextLength } from "@postdaily/api/lib/provider-text-length";

import type { PlatformId } from "@/components/social-accounts/provider-icon";
import { charLimitForProvider } from "./provider-meta";

/**
 * Count characters the way the active network will validate them. This is the
 * same function the server's draft validation uses (`providerTextLength`), so
 * the ring always matches what scheduling accepts.
 */
export function countCharsForProvider(
  provider: PlatformId | string | null | undefined,
  text: string,
): number {
  return providerTextLength(provider ?? "", text);
}

/** Tightest display limit among the given providers (All-channels HUD). */
export function charLimitForProviders(
  providers: Array<PlatformId | null | undefined>,
): number | null {
  let min: number | null = null;
  for (const provider of providers) {
    if (!provider) continue;
    const limit = charLimitForProvider(provider);
    min = min === null ? limit : Math.min(min, limit);
  }
  return min;
}

/** A channel, with its own text limit when the connection has one. */
type LimitedAccount = {
  provider: PlatformId;
  /**
   * The connection's own limit (a Mastodon server's `max_characters`), from
   * the accounts list. Absent or null means the network's default.
   */
  maxTextLength?: number | null;
};

/** This channel's limit: its own when known, else the network's. */
export function charLimitForAccount(account: LimitedAccount): number {
  return account.maxTextLength ?? charLimitForProvider(account.provider);
}

/** Tightest limit among these channels, each using its own when known. */
export function charLimitForAccounts(
  accounts: ReadonlyArray<LimitedAccount>,
): number | null {
  let min: number | null = null;
  for (const account of accounts) {
    const limit = charLimitForAccount(account);
    min = min === null ? limit : Math.min(min, limit);
  }
  return min;
}

/**
 * True when `text` is too long for any of these channels, counted the way each
 * network counts and measured against each channel's own limit.
 */
export function textExceedsAccountLimits(
  accounts: ReadonlyArray<LimitedAccount>,
  text: string,
): boolean {
  return accounts.some(
    (account) =>
      countCharsForProvider(account.provider, text) >
      charLimitForAccount(account),
  );
}
